import React from "react";
import { Field, reduxForm, change } from "redux-form";
import {
	Form,
	Button,
	Typography,
	Space,
	Divider,
	Tooltip,
} from "antd";
import { MessageOutlined, MailOutlined, MobileOutlined } from "@ant-design/icons";
import {
	getSignupState,
	getOtpKey,
	getOtpMethod,
	getOtpMobilePhone,
	getOtpMobileRegionCode,
	getOtpMobileRegionShort,
	getOtpEmail,
} from "./reducers";
import { getCurrentLocation } from "../Location/reducers";
import { InputField, InternationalPhoneField } from "components/AntdReduxForm";
import { required } from "redux-form-validators";
import { connect, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { isMobile } from "react-device-detect";
import { generateOtpRequest } from "./actions";
import { trackEvent, trackButtonClick } from "utils/analytics";

const { Text } = Typography;

const RESEND_COOLDOWN_SECONDS = 120;

function GuestForm({
	handleSubmit,
	signupState,
	otpKey,
	otpMethod,
	otpEmail,
	otpKeyMobilePhone,
	otpKeyMobileRegionCode,
	otpKeyMobileRegionShort,
	dispatch,
	handleResubmit,
	codeVerificationMethod,
	setCodeVerificationMethod,
	clearOtpKey,
	currentLocation,
}) {
	const [valid, setValid] = React.useState(false);
	const [smsCooldown, setSmsCooldown] = React.useState(0);
	const cooldownRef = React.useRef(null);

	const startSmsCooldown = React.useCallback(() => {
		setSmsCooldown(RESEND_COOLDOWN_SECONDS);
		cooldownRef.current = setInterval(() => {
			setSmsCooldown((prev) => {
				if (prev <= 1) {
					clearInterval(cooldownRef.current);
					cooldownRef.current = null;
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, []);

	React.useEffect(() => {
		return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
	}, []);

	const formatCooldown = (seconds) => {
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return `${m}:${String(s).padStart(2, '0')}`;
	};

	const setRegionCode = (value) => {
		dispatch(change("GuestForm", "mobileRegionCode", value.code));
	};

	const setRegionShort = (value) => {
		dispatch(change("GuestForm", "mobileRegionShort", value.short));
	};

	const mobileRegionCode = useSelector(
		(state) =>
			state.locations.currentLocation.practiceLocalisationSetting
				.mobileRegionCode,
	);

	const mobileRegionShort = useSelector(
		(state) =>
			state.locations.currentLocation.practiceLocalisationSetting
				.mobileRegionShort,
	);

	const handleValidCheck = React.useCallback(
		(value) => {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			const phoneRegex = /^\d{8,10}$/;

			if (codeVerificationMethod === "sms") {
				setValid(phoneRegex.test(value.replace(/\s/g, "")));
			} else {
				setValid(emailRegex.test(value));
			}
		},
		[codeVerificationMethod],
	);

	const onSendCode = () => {
		if (codeVerificationMethod === "sms") {
			startSmsCooldown();
		}
		trackButtonClick(otpKey ? 'verify_otp_button' : 'send_otp_button', {
			method: codeVerificationMethod,
		});
		handleSubmit();
	};

	const onResend = () => {
		startSmsCooldown();
		trackButtonClick('resend_otp_button', { method: codeVerificationMethod });
		handleResubmit({
			mobilePhone: otpKeyMobilePhone,
			mobileRegionCode: otpKeyMobileRegionCode,
			mobileRegionShort: otpKeyMobileRegionShort,
			otpMethod,
		});
	};

	const resendAddress = React.useMemo(
		() =>
			({
				sms: otpKeyMobilePhone,
				email: otpEmail,
			})[otpMethod],
		[otpEmail, otpKeyMobilePhone, otpMethod],
	);

	return (
		<Form layout="vertical">
			<div
				style={{
					maxWidth: "500px",
					marginLeft: "auto",
					marginRight: "auto",
					textAlign: "center",
				}}
			>
				<Typography.Title level={isMobile ? 5 : 3}>
					We are just checking you're human
				</Typography.Title>

				{otpKey ? (
					// OTP Verification State
					<div>
						<Field
							size="large"
							name="otp"
							placeholder="Enter Code"
							validate={[required()]}
							prefix={<MessageOutlined />}
							label={<>We have sent a code to {resendAddress}</>}
							component={InputField}
							autocomplete="off"
							style={{ maxWidth: 550 }}
							required
						/>
						<Button
							block={isMobile}
							type="primary"
							loading={signupState}
							htmlType="button"
							onClick={onSendCode}
							style={{
								marginBottom: 16,
								backgroundColor: currentLocation?.themeColor,
								borderColor: currentLocation?.themeColor,
							}}
						>
							Verify
						</Button>

						<Divider dashed style={{ margin: '16px 0' }} />

						<Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
							Didn't receive your code?
						</Text>

						<Space wrap>
							{/* Only show Resend for SMS */}
							{codeVerificationMethod === "sms" && (
								<Tooltip
									title={
										smsCooldown === 0
											? `Resend to ${resendAddress}`
											: `Please wait ${formatCooldown(smsCooldown)} before resending`
									}
								>
									<Button
										disabled={smsCooldown > 0}
										type="default"
										loading={signupState}
										htmlType="button"
										onClick={onResend}
									>
										{smsCooldown > 0 ? `Resend in ${formatCooldown(smsCooldown)}` : 'Resend Code'}
									</Button>
								</Tooltip>
							)}

							<Tooltip
								title={
									codeVerificationMethod === "sms" && smsCooldown > 0
										? `Please wait ${formatCooldown(smsCooldown)} before switching back to SMS`
										: undefined
								}
							>
								<Button
									type="default"
									disabled={codeVerificationMethod === "email" && smsCooldown > 0}
									onClick={() => {
										if (codeVerificationMethod === "sms") {
											clearOtpKey();
											setCodeVerificationMethod("email");
											setValid(false);
											dispatch(change("GuestForm", "mobilePhone", ""));
											trackEvent("otp_method_switched", { from: "sms", to: "email" });
										} else if (smsCooldown === 0) {
											clearOtpKey();
											setCodeVerificationMethod("sms");
											setValid(false);
											dispatch(change("GuestForm", "email", ""));
											trackEvent("otp_method_switched", { from: "email", to: "sms" });
										}
									}}
								>
									{codeVerificationMethod === "sms" ? "Try Email Instead" : (
										smsCooldown > 0
											? `SMS available in ${formatCooldown(smsCooldown)}`
											: "Try SMS Instead"
									)}
								</Button>
							</Tooltip>
						</Space>
					</div>
				) : (
					// Initial Input State
					<div>
						{/* Verification Method Toggle */}
						<div style={{
							display: 'flex',
							marginBottom: 20,
							background: '#f5f5f5',
							borderRadius: 8,
							padding: 4,
						}}>
							<button
								type="button"
								disabled={smsCooldown > 0 && codeVerificationMethod !== "sms"}
								onClick={() => {
									if (codeVerificationMethod !== "sms" && smsCooldown === 0) {
										clearOtpKey();
										setCodeVerificationMethod("sms");
										setValid(false);
										dispatch(change("GuestForm", "email", ""));
										trackEvent("otp_method_switched", { from: "email", to: "sms" });
									}
								}}
								style={{
									flex: 1,
									padding: '10px 16px',
									border: 'none',
									borderRadius: 6,
									cursor: smsCooldown > 0 && codeVerificationMethod !== "sms" ? 'not-allowed' : 'pointer',
									background: codeVerificationMethod === "sms" ? '#fff' : 'transparent',
									boxShadow: codeVerificationMethod === "sms" ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
									fontWeight: codeVerificationMethod === "sms" ? 500 : 400,
									color: smsCooldown > 0 && codeVerificationMethod !== "sms" ? '#bbb' : codeVerificationMethod === "sms" ? '#333' : '#666',
									transition: 'all 0.2s',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: 8,
								}}
							>
								<MobileOutlined />
								{smsCooldown > 0 && codeVerificationMethod !== "sms" ? `SMS (${formatCooldown(smsCooldown)})` : 'SMS'}
							</button>
							<button
								type="button"
								onClick={() => {
									if (codeVerificationMethod !== "email") {
										clearOtpKey();
										setCodeVerificationMethod("email");
										setValid(false);
										dispatch(change("GuestForm", "mobilePhone", ""));
										trackEvent("otp_method_switched", { from: "sms", to: "email" });
									}
								}}
								style={{
									flex: 1,
									padding: '10px 16px',
									border: 'none',
									borderRadius: 6,
									cursor: 'pointer',
									background: codeVerificationMethod === "email" ? '#fff' : 'transparent',
									boxShadow: codeVerificationMethod === "email" ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
									fontWeight: codeVerificationMethod === "email" ? 500 : 400,
									color: codeVerificationMethod === "email" ? '#333' : '#666',
									transition: 'all 0.2s',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: 8,
								}}
							>
								<MailOutlined />
								Email
							</button>
						</div>

						{/* Input Field */}
						{codeVerificationMethod === "sms" ? (
							<Field
								size="large"
								name="mobilePhone"
								validate={[required()]}
								placeholder=""
								label="Enter your mobile number"
								component={InternationalPhoneField}
								autocomplete="off"
								setRegionCode={setRegionCode}
								setRegionShort={setRegionShort}
								accountRegionShort={mobileRegionShort}
								accountRegionCode={mobileRegionCode}
								onChange={(e) => handleValidCheck(e)}
								required
								style={{ maxWidth: 550 }}
							/>
						) : (
							<Field
								size="large"
								name="email"
								validate={[required()]}
								placeholder="example@example.com"
								label="Enter your email address"
								component={InputField}
								autocomplete="off"
								required
								onChange={(e) => handleValidCheck(e.target.value)}
								style={{ maxWidth: 550 }}
							/>
						)}

						{/* Send Code Button */}
						<Button
							type="primary"
							size="large"
							block
							loading={signupState}
							disabled={!valid}
							onClick={onSendCode}
							style={{
								marginTop: 16,
								backgroundColor: valid ? currentLocation?.themeColor : undefined,
								borderColor: valid ? currentLocation?.themeColor : undefined,
							}}
						>
							Send Code
						</Button>
					</div>
				)}
			</div>
		</Form>
	);
}

const mapDispatchToProps = (dispatch) =>
	bindActionCreators({ change }, dispatch);

const mapStateToProps = (state) => ({
	signupState: getSignupState(state.session),
	otpKey: getOtpKey(state.session),
	otpMethod: getOtpMethod(state.session),
	otpEmail: getOtpEmail(state.session),
	otpKeyMobilePhone: getOtpMobilePhone(state.session),
	otpKeyMobileRegionCode: getOtpMobileRegionCode(state.session),
	otpKeyMobileRegionShort: getOtpMobileRegionShort(state.session),
	currentLocation: getCurrentLocation(state.locations),
	generateOtpRequest,
});

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(
	reduxForm({
		form: "GuestForm",
	})(GuestForm),
);